import { notification } from 'antd';

/**
 * Complemento del ErrorBoundary.
 *
 * React solo propaga hasta un boundary los errores que ocurren durante el
 * render. Los que se lanzan dentro de un `onClick`, un `onChange` o cualquier
 * callback asíncrono quedan sueltos: hoy no los ve nadie y el usuario se queda
 * sin ninguna señal de que su acción falló. Estos handlers los capturan a nivel
 * de `window` y muestran un aviso.
 *
 * Se avisa con una notificación y no con la pantalla completa a propósito: si
 * falló un manejador de evento el resto de la app sigue siendo usable, y
 * sacarle al usuario la pantalla de donde está sería peor que el problema.
 */

const NOTIFICATION_KEY = 'error-inesperado';

let registered = false;

function isAxiosError(error: unknown): boolean {
  return !!(error as { isAxiosError?: boolean })?.isAxiosError;
}

/**
 * Texto técnico del error, para que el usuario pueda transmitirlo al pedir
 * soporte. En producción no hay consola a mano ni overlay, así que este es el
 * único rastro que queda de qué falló realmente.
 */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.name ? `${error.name}: ${error.message}` : error.message;
  }
  if (typeof error === 'string') return error;

  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

function notifyUnexpectedError(error: unknown, origen: string) {
  // Los errores HTTP ya tienen su propio camino: el interceptor de umi-request
  // redirige a /500 y cada pantalla maneja el resto de los status.
  if (isAxiosError(error)) return;

  // Los rechazos con un string son control de flujo interno de umi-request
  // durante el logout ('Sin token', 'Refresh token expirado'). No son fallas
  // que le interesen al usuario: se está yendo a la pantalla de ingreso.
  if (typeof error === 'string') {
    console.warn(`[${origen}] rechazo controlado:`, error);
    return;
  }

  console.error(`[${origen}] error inesperado:`, error);

  notification.error({
    key: NOTIFICATION_KEY,
    message: 'Algo no funcionó como esperábamos',
    duration: 8,
    description: `No pudimos completar la acción. Si vuelve a pasar, recargá la página e intentá de nuevo.\n\nDetalle: ${describeError(error)}`,
    style: { whiteSpace: 'pre-line' },
  });
}

export function registerGlobalErrorHandlers() {
  if (registered || typeof window === 'undefined') return;
  registered = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    if (event.target && event.target !== window) return;

    notifyUnexpectedError(event.error ?? event.message, 'window.error');
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    notifyUnexpectedError(event.reason, 'unhandledrejection');
  });
}
